import { createContext, ReactNode, useContext, useMemo, useState } from 'react';
import { portalMockData } from './portalMockData';
import { PortalBacklogItem, PortalComment, PortalFeedback, PortalPreviewData, PortalRoadmapItem } from './portalTypes';

type PortalStateValue = {
  data: PortalPreviewData;
  needsYouCount: number;
  openFeedback: PortalFeedback[];
  threadsAwaitingReply: PortalComment[];
  nextMilestone: PortalRoadmapItem | null;
  addClientComment: (targetId: string, body: string) => void;
  suggestDemand: (projectId: string, title: string) => string | null;
  submitFeedback: (feedbackId: string, score: number, comment: string) => void;
  markAllNotificationsRead: () => void;
  markNotificationRead: (notificationId: string) => void;
  updateProfile: (name: string, email: string) => void;
  markThreadResolved: (targetId: string) => void;
};

const PortalStateContext = createContext<PortalStateValue | null>(null);

function latestCommentByTarget(comments: PortalComment[]): PortalComment[] {
  const latest = new Map<string, PortalComment>();
  for (const comment of comments) {
    const key = `${comment.targetType}:${comment.targetId}`;
    const current = latest.get(key);
    if (!current || comment.createdAt > current.createdAt) {
      latest.set(key, comment);
    }
  }
  return Array.from(latest.values());
}

export function PortalStateProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<PortalPreviewData>(portalMockData);

  const threadsAwaitingReply = useMemo(
    () =>
      latestCommentByTarget(data.comments).filter(
        (comment) => !comment.fromClient && !data.resolvedCommentTargets.includes(comment.targetId),
      ),
    [data.comments, data.resolvedCommentTargets],
  );

  const openFeedback = useMemo(
    () => data.feedback.filter((item) => !item.submitted),
    [data.feedback],
  );

  const nextMilestone = useMemo(() => {
    const upcoming = data.roadmap
      .filter((item) => item.status === 'planned' || item.status === 'inProgress')
      .slice()
      .sort((left, right) => left.plannedDate.localeCompare(right.plannedDate));
    return upcoming[0] ?? null;
  }, [data.roadmap]);

  const value = useMemo<PortalStateValue>(() => {
    return {
      data,
      needsYouCount: openFeedback.length + threadsAwaitingReply.length,
      openFeedback,
      threadsAwaitingReply,
      nextMilestone,
      addClientComment: (targetId, body) => {
        const trimmed = body.trim();
        if (!trimmed) return;
        const target = data.comments.find((comment) => comment.targetId === targetId);
        const backlog = data.backlog.find((item) => item.id === targetId);
        const roadmap = data.roadmap.find((item) => item.id === targetId);
        const projectId = target?.projectId ?? backlog?.projectId ?? roadmap?.projectId;
        const targetType = target?.targetType ?? (backlog ? 'backlog' : 'roadmap');
        if (!projectId) return;
        const next: PortalComment = {
          id: `c-${Date.now()}`,
          projectId,
          targetType,
          targetId,
          authorName: data.clientName,
          fromClient: true,
          body: trimmed,
          createdAt: new Date().toISOString(),
        };
        setData((current) => ({ ...current, comments: [...current.comments, next] }));
      },
      suggestDemand: (projectId, title) => {
        const trimmed = title.trim();
        if (!trimmed) return null;
        const id = `b-${Date.now()}`;
        const reference = `NOR-${100 + data.backlog.filter((item) => item.projectId === projectId).length}`;
        const next: PortalBacklogItem = {
          id,
          projectId,
          reference,
          title: trimmed,
          description: 'Sugestão enviada pela área do cliente.',
          priorityLabel: 'Média',
          ownerName: data.clientName.split(' ')[0] || data.clientName,
          status: 'idea',
          labels: [],
          inActiveSprint: false,
          sprintId: null,
          startDate: null,
          endDate: null,
          commitment: null,
        };
        setData((current) => ({ ...current, backlog: [...current.backlog, next] }));
        return id;
      },
      submitFeedback: (feedbackId, score, comment) => {
        setData((current) => ({
          ...current,
          feedback: current.feedback.map((item) =>
            item.id === feedbackId
              ? { ...item, submitted: true, score, comment: comment.trim() }
              : item,
          ),
        }));
      },
      markAllNotificationsRead: () => {
        setData((current) => ({
          ...current,
          activity: current.activity.map((item) => ({ ...item, read: true })),
        }));
      },
      markNotificationRead: (notificationId) => {
        setData((current) => ({
          ...current,
          activity: current.activity.map((item) =>
            item.id === notificationId ? { ...item, read: true } : item,
          ),
        }));
      },
      markThreadResolved: (targetId) => {
        setData((current) => ({
          ...current,
          resolvedCommentTargets: current.resolvedCommentTargets.includes(targetId)
            ? current.resolvedCommentTargets
            : [...current.resolvedCommentTargets, targetId],
        }));
      },
      updateProfile: (name, email) => {
        const trimmedName = name.trim();
        const trimmedEmail = email.trim();
        if (!trimmedName || !trimmedEmail) return;
        setData((current) => ({
          ...current,
          clientName: trimmedName,
          clientEmail: trimmedEmail,
        }));
      },
    };
  }, [data, nextMilestone, openFeedback, threadsAwaitingReply]);

  return <PortalStateContext.Provider value={value}>{children}</PortalStateContext.Provider>;
}

export function usePortalState(): PortalStateValue {
  const value = useContext(PortalStateContext);
  if (!value) {
    throw new Error('usePortalState deve ser usado dentro do portal.');
  }
  return value;
}
